import { validators } from '@vivaldis/common'
import { EmployeeInput } from '@vivaldis/graphql'
import {
  Formik,
  FormikContextType,
  FormikHelpers,
  useFormikContext
} from 'formik'
import { PropsWithChildren, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { Employee } from '../../../types/Employee'

export interface FormValues {
  firstName: string
  lastName: string
  nationalityCountryId: string | null
  ssn: string
  // -> id card
  idCardNumber: string | null
  idCardExpiresOn: Date | null
  idCardFrontPicture: string | null
  idCardBackPicture: string | null
  // residence permit
  residencePermitExpiresOn: Date | null
  residencePermitCode: string | null
  residencePermitFrontPicture: string | null
  residencePermitBackPicture: string | null
  // work permit
  workPermitExpiresOn: Date | null
  workPermitFrontPicture: string | null
  workPermitBackPicture: string | null
}

export function useForm(): FormikContextType<FormValues> {
  return useFormikContext()
}

export interface EmployeeFormProviderProps {
  employee: Employee
  onGoBack: () => void
  onSubmit: (
    employeeInput: EmployeeInput,
    formikHelpers: FormikHelpers<FormValues>
  ) => Promise<void>
}

export const FormProvider = ({
  employee,
  onSubmit,
  onGoBack,
  children
}: PropsWithChildren<EmployeeFormProviderProps>) => {
  const [t] = useTranslation('web_ui')

  const initialFormValues: FormValues = useMemo(
    () => ({
      firstName: employee.firstName || '',
      lastName: employee.lastName || '',
      nationalityCountryId: employee.profile?.nationalityCountry?.id || null,
      ssn: employee.ssn || '',
      // -> id card
      idCardNumber: employee.profile?.idCardNumber || null,
      idCardExpiresOn: employee.profile?.idCardExpiresOn
        ? new Date(employee.profile.idCardExpiresOn)
        : null,
      idCardFrontPicture: employee.profile?.idCardFrontPicture || null,
      idCardBackPicture: employee.profile?.idCardBackPicture || null,
      // residence permit
      residencePermitFrontPicture:
        employee.workProfile?.residencePermitFrontPicture || null,
      residencePermitBackPicture:
        employee.workProfile?.residencePermitBackPicture || null,
      residencePermitExpiresOn: employee.workProfile?.residencePermitExpiresOn
        ? new Date(employee.workProfile.residencePermitExpiresOn)
        : null,
      residencePermitCode: employee.workProfile?.residencePermitCode || null,
      // ->  work permit
      workPermitFrontPicture:
        employee.workProfile?.workPermitFrontPicture || null,
      workPermitBackPicture:
        employee.workProfile?.workPermitBackPicture || null,
      workPermitExpiresOn: employee.workProfile?.workPermitExpiresOn
        ? new Date(employee.workProfile.workPermitExpiresOn)
        : null
    }),
    [
      employee.firstName,
      employee.lastName,
      employee.profile?.idCardBackPicture,
      employee.profile?.idCardExpiresOn,
      employee.profile?.idCardFrontPicture,
      employee.profile?.idCardNumber,
      employee.profile?.nationalityCountry?.id,
      employee.ssn,
      employee.workProfile?.residencePermitBackPicture,
      employee.workProfile?.residencePermitCode,
      employee.workProfile?.residencePermitExpiresOn,
      employee.workProfile?.residencePermitFrontPicture,
      employee.workProfile?.workPermitBackPicture,
      employee.workProfile?.workPermitExpiresOn,
      employee.workProfile?.workPermitFrontPicture
    ]
  )

  const ValidationSchema = useMemo(
    () =>
      Yup.object().shape({
        ssn: Yup.string()
          .label(
            t(
              'edit_employee_personal_information_form.steps.enter_ssn.ssn.label'
            )
          )
          .nullable()
          .required()
          .test(
            'isValidSSN',
            t(
              'edit_employee_personal_information_form.steps.enter_ssn.ssn.errors.invalid'
            ),
            ssn => (ssn ? validators.isValidSSN(ssn) : true)
          )
      }),
    [t]
  )

  const handleSubmit = useCallback(
    async (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
      await onSubmit(
        {
          // on this screen only ssn is editable
          ssn: values.ssn
        },
        formikHelpers
      )
      formikHelpers.setSubmitting(false)
      onGoBack()
    },
    [onGoBack, onSubmit]
  )

  return (
    <Formik<FormValues>
      initialValues={initialFormValues}
      validateOnChange={false}
      validateOnBlur={true}
      validationSchema={ValidationSchema}
      onSubmit={handleSubmit}
    >
      {children}
    </Formik>
  )
}
